import { ChevronRight } from 'lucide-react';
import { useMemo } from 'react';
import { useIntl } from '@/core/i18n/use-intl';
import { Button } from '@/shared/components/ui';
import { cn } from '@/shared/utils';

export type ContractSidemenuItem = {
  id: string;
  label: string;
  kind: 'master-data' | 'metadata-group' | 'custom-group';
  hasError?: boolean;
};

type ContractDetailsSidemenuProps = {
  items: ContractSidemenuItem[];
  activeId: string | null;
  onSelect: (id: string) => void;
  showCustomSeparator?: boolean;
};

export function ContractDetailsSidemenu({
  items,
  activeId,
  onSelect,
  showCustomSeparator,
}: ContractDetailsSidemenuProps) {
  const { t } = useIntl();

  const { masterData, metadataGroups, customGroups } = useMemo(() => {
    const masterData = items.filter((i) => i.kind === 'master-data');
    const metadataGroups = items.filter((i) => i.kind === 'metadata-group');
    const customGroups = items.filter((i) => i.kind === 'custom-group');
    return { masterData, metadataGroups, customGroups };
  }, [items]);

  const renderItemButton = (item: ContractSidemenuItem) => {
    const isActive = activeId === item.id;
    return (
      <Button
        key={item.id}
        type="button"
        variant="ghost"
        className={cn(
          'w-full justify-between px-3 py-2 h-auto text-sm font-medium rounded-lg',
          isActive
            ? 'bg-muted text-foreground hover:bg-muted'
            : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
        )}
        onClick={() => onSelect(item.id)}
      >
        <span className="truncate flex items-center gap-2">
          {item.hasError ? <span className="size-2 rounded-full bg-destructive" /> : null}
          {item.label}
        </span>
        <ChevronRight className={cn('size-4 shrink-0', isActive ? 'opacity-100' : 'opacity-40')} />
      </Button>
    );
  };

  return (
    <nav className="space-y-2">
      <div className="space-y-1">{masterData.map(renderItemButton)}</div>

      {metadataGroups.length > 0 && (
        <div className="space-y-1">{metadataGroups.map(renderItemButton)}</div>
      )}

      {showCustomSeparator && customGroups.length > 0 && (
        <div className="pt-3">
          <div className="h-px bg-border mb-6" />
          <div className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            {t('metadata.customMetadata')}
          </div>
        </div>
      )}

      {customGroups.length > 0 && (
        <div className="space-y-1">{customGroups.map(renderItemButton)}</div>
      )}
    </nav>
  );
}
